import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Fab
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Add as AddIcon, Upload as UploadIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface PollData {
  poll_unique_id: number;
  poll_week: number;
  poll_year: number;
  poll_date: string;
  poll_rank: number;
  poll_prev: number | null;
  poll_school: string;
  poll_fpv: number | null;
  poll_points: number | null;
  poll_teams: number;
  poll_status: string;
  poll_atp: number | null;
}

const DataManagement: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<PollData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    year: '',
    week: '',
    school: ''
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<PollData | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<PollData>>({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState<Partial<PollData>>({});
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: rowsPerPage.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const url = `https://basketbase-workers.bakonydraco1.workers.dev/api/admin/data?${params}`;
      console.log('Fetching data from:', url);
      
      const token = localStorage.getItem('admin_token');
      console.log('Using token:', token ? 'present' : 'missing');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Successfully fetched data:', result);
      setData(result.data);
      setTotalCount(result.total);
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchData();
  }, [page, filters, fetchData]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filtering
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      const response = await fetch(`https://basketbase-workers.bakonydraco1.workers.dev/api/admin/data/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete record');
      }

      fetchData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete record');
    }
  };

  const handleEdit = (row: PollData) => {
    setEditingRow(row);
    setEditFormData({ ...row });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRow) return;

    try {
      const response = await fetch(`https://basketbase-workers.bakonydraco1.workers.dev/api/admin/data/${editingRow.poll_unique_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(editFormData)
      });

      if (!response.ok) {
        throw new Error('Failed to update record');
      }

      setEditDialogOpen(false);
      setEditingRow(null);
      fetchData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update record');
    }
  };

  const handleAdd = () => {
    setAddFormData({
      poll_week: 1,
      poll_year: new Date().getFullYear(),
      poll_date: new Date().toISOString().split('T')[0],
      poll_rank: 1,
      poll_prev: null,
      poll_school: '',
      poll_fpv: null,
      poll_points: 0,
      poll_teams: 25,
      poll_status: 'RK',
      poll_atp: null
    });
    setAddDialogOpen(true);
  };

  const handleSaveAdd = async () => {
    try {
      const response = await fetch('https://basketbase-workers.bakonydraco1.workers.dev/api/admin/data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(addFormData)
      });

      if (!response.ok) {
        throw new Error('Failed to add record');
      }

      setAddDialogOpen(false);
      setAddFormData({});
      setSuccess('Record added successfully!');
      setError(''); // Clear any previous errors
      fetchData(); // Refresh data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add record');
      setSuccess(''); // Clear any previous success messages
    }
  };

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCsvFile(file);
      parseCsvFile(file);
    }
  };

  const parseCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length === 0) return;
      
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });
      
      setCsvPreview(rows.slice(0, 5)); // Show first 5 rows as preview
    };
    reader.readAsText(file);
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;

    try {
      const formData = new FormData();
      formData.append('csv', csvFile);

      const response = await fetch('https://basketbase-workers.bakonydraco1.workers.dev/api/admin/data/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to import CSV');
      }

      const result = await response.json();
      setCsvDialogOpen(false);
      setCsvFile(null);
      setCsvPreview([]);
      setSuccess(`Successfully imported ${result.imported} records`);
      setError(''); // Clear any previous errors
      fetchData(); // Refresh data
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import CSV');
      setSuccess(''); // Clear any previous success messages
    }
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Poll Data Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setCsvDialogOpen(true)}
        >
          Import CSV
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Year"
            value={filters.year}
            onChange={(e) => handleFilterChange('year', e.target.value)}
            size="small"
            type="number"
          />
          <TextField
            label="Week"
            value={filters.week}
            onChange={(e) => handleFilterChange('week', e.target.value)}
            size="small"
            type="number"
          />
          <TextField
            label="School"
            value={filters.school}
            onChange={(e) => handleFilterChange('school', e.target.value)}
            size="small"
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Week</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Rank</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>FPV</TableCell>
                  <TableCell>Points</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.poll_unique_id}>
                    <TableCell>{row.poll_unique_id}</TableCell>
                    <TableCell>{row.poll_year}</TableCell>
                    <TableCell>{row.poll_week}</TableCell>
                    <TableCell>{row.poll_date}</TableCell>
                    <TableCell>{row.poll_rank}</TableCell>
                    <TableCell>{row.poll_school}</TableCell>
                    <TableCell>{row.poll_fpv || '-'}</TableCell>
                    <TableCell>{row.poll_points}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.poll_status} 
                        color={row.poll_status === 'RK' ? 'primary' : 'secondary'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user?.isAdmin && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(row)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {user?.isAdmin && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(row.poll_unique_id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        </>
      )}

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        onClick={handleAdd}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
      >
        <AddIcon />
      </Fab>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Poll Record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Year"
              type="number"
              value={editFormData.poll_year || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_year: parseInt(e.target.value) })}
            />
            <TextField
              label="Week"
              type="number"
              value={editFormData.poll_week || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_week: parseInt(e.target.value) })}
            />
            <TextField
              label="Date"
              type="date"
              value={editFormData.poll_date || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Rank"
              type="number"
              value={editFormData.poll_rank || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_rank: parseInt(e.target.value) })}
            />
            <TextField
              label="School"
              value={editFormData.poll_school || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_school: e.target.value })}
            />
            <TextField
              label="Points"
              type="number"
              value={editFormData.poll_points || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_points: parseInt(e.target.value) })}
            />
            <TextField
              label="First Place Votes"
              type="number"
              value={editFormData.poll_fpv || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_fpv: parseInt(e.target.value) || null })}
            />
            <TextField
              label="Status"
              select
              value={editFormData.poll_status || ''}
              onChange={(e) => setEditFormData({ ...editFormData, poll_status: e.target.value })}
            >
              <option value="RK">Ranked</option>
              <option value="RV">Receiving Votes</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Poll Record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Year"
              type="number"
              value={addFormData.poll_year || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_year: parseInt(e.target.value) })}
            />
            <TextField
              label="Week"
              type="number"
              value={addFormData.poll_week || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_week: parseInt(e.target.value) })}
            />
            <TextField
              label="Date"
              type="date"
              value={addFormData.poll_date || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Rank"
              type="number"
              value={addFormData.poll_rank || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_rank: parseInt(e.target.value) })}
            />
            <TextField
              label="School"
              value={addFormData.poll_school || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_school: e.target.value })}
            />
            <TextField
              label="Points"
              type="number"
              value={addFormData.poll_points || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_points: parseInt(e.target.value) })}
            />
            <TextField
              label="First Place Votes"
              type="number"
              value={addFormData.poll_fpv || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_fpv: parseInt(e.target.value) || null })}
            />
            <TextField
              label="Status"
              select
              value={addFormData.poll_status || ''}
              onChange={(e) => setAddFormData({ ...addFormData, poll_status: e.target.value })}
            >
              <option value="RK">Ranked</option>
              <option value="RV">Receiving Votes</option>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAdd} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import CSV Data</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV file with poll data. The CSV must have these exact columns in this order:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              poll_week,poll_year,poll_date,poll_rank,poll_prev,poll_school,poll_fpv,poll_points,poll_teams,poll_status,poll_atp
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Example: 1,2025,2025-01-01,1,,Kansas,30,1500,25,RK,25
            </Typography>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleCsvFileChange}
              style={{ marginBottom: '16px' }}
            />
            
            {csvPreview.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Preview (first 5 rows):
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {Object.keys(csvPreview[0] || {}).map((header) => (
                          <TableCell key={header}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {csvPreview.map((row, index) => (
                        <TableRow key={index}>
                          {Object.values(row).map((value: any, cellIndex) => (
                            <TableCell key={cellIndex}>{value}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCsvImport} 
            variant="contained"
            disabled={!csvFile}
          >
            Import CSV
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataManagement;
